import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Grid,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    PlayArrow as PlayIcon,
    People as PeopleIcon,
    Storage as StorageIcon,
    TableChart as ExcelIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import { supplierStore, authStore } from "../stores";
import { statsStore } from "../stores";
import Constants from "../constants";
import { exportToCsv, exportToExcel, formatDateForExcel } from "../utils/excelExport";
import { resolveStatsSupplierId } from "../utils/statsSupplier";

interface MediaStatsProps {
    startDate?: string;
    endDate?: string;
    clientId?: number;
}

interface MediaStat {
    mediaTitle: string;
    mediaUrl: string;
    mediaId: string | number;
    actualMediaId?: string | number;
    actualMediaName?: string;
    actualMediaLink?: string | null; // Ссылка эпизода
    actualParentId?: string | number | null;
    parentMediaId?: string | number | null;
    parentMediaName?: string | null;
    parentMediaLink?: string | null; // Ссылка сезона
    parentMediaParentId?: string | number | null;
    grandparentMediaId?: string | number | null;
    grandparentMediaName?: string | null;
    grandparentMediaLink?: string | null; // Ссылка сериала
    clientName: string;
    viewCount: number | string;
    uniqueViewers: number | string;
    groupUniqueViewers?: number | string | null;
    totalBytes: number | string;
    firstView: string;
    lastView: string;
}

const parseOptionalNumber = (value: unknown): number | null => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
};

const MediaStats: React.FC<MediaStatsProps> = observer(({ startDate, endDate, clientId }) => {
    const [stats, setStats] = useState<MediaStat[]>([]);
    const [totalViewCount, setTotalViewCount] = useState<number>(0);
    const [totalUniqueViewersCount, setTotalUniqueViewersCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedSeries, setExpandedSeries] = useState<Set<string>>(new Set()); // Для хранения раскрытых сериалов

    const isClient = authStore.user && !["okko", "wink", "premiere", "admin"].includes(authStore.user.login);
    const isAdmin = authStore.user?.login === "admin";

    const formatMediaDbIds = (stat: MediaStat): string => {
        const ids: string[] = [];
        const actualId = stat.actualMediaId || stat.mediaId;

        if (actualId) {
            ids.push(`Медиа #${actualId}`);
        }
        if (stat.parentMediaId) {
            ids.push(`Сезон #${stat.parentMediaId}`);
        }
        if (stat.grandparentMediaId) {
            ids.push(`Сериал #${stat.grandparentMediaId}`);
        }
        if (stat.mediaId && stat.mediaId !== actualId) {
            ids.push(`Группировка #${stat.mediaId}`);
        }

        return ids.join("; ");
    };

    const renderMediaDbIds = (stat: MediaStat) => {
        const actualId = stat.actualMediaId || stat.mediaId;

        return (
            <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
                {actualId && <Chip label={`Медиа #${actualId}`} size="small" color="primary" variant="outlined" />}
                {stat.parentMediaId && <Chip label={`Сезон #${stat.parentMediaId}`} size="small" variant="outlined" />}
                {stat.grandparentMediaId && (
                    <Chip label={`Сериал #${stat.grandparentMediaId}`} size="small" color="secondary" variant="outlined" />
                )}
                {stat.mediaId && stat.mediaId !== actualId && (
                    <Typography variant="caption" color="text.secondary">
                        Группировка #{stat.mediaId}
                    </Typography>
                )}
            </Box>
        );
    };

    const fetchStats = async () => {
        const supplierId = resolveStatsSupplierId(supplierStore.currentSupplier, authStore.user);
        if (!supplierId || !startDate || !endDate) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await statsStore.fetchMediaStats(clientId ?? null, new Date(startDate), new Date(endDate), supplierId);
            const cached = statsStore.mediaStats as any;
            const mediaData = Array.isArray(cached?.data) ? cached.data : [];
            const totalViewCount = cached?.totalViewCount || 0;
            const totalUniqueViewers =
                cached?.totalUniqueViewers === undefined ? null : Number(cached.totalUniqueViewers);
            // Нормализуем данные - преобразуем строки в числа где нужно
            const normalizedData = mediaData.map((item: any) => ({
                ...item,
                viewCount: typeof item.viewCount === 'string' ? parseInt(item.viewCount) : item.viewCount,
                uniqueViewers: typeof item.uniqueViewers === 'string' ? parseInt(item.uniqueViewers) : item.uniqueViewers,
                totalBytes: typeof item.totalBytes === 'string' ? parseInt(item.totalBytes) : item.totalBytes,
            }));
            setStats(normalizedData);
            setTotalViewCount(totalViewCount);
            setTotalUniqueViewersCount(totalUniqueViewers);
        } catch (err) {
            setError("Ошибка загрузки статистики медиа");
            console.error("Error fetching media stats:", err);
        } finally {
            setLoading(false);
        }
    };

    const exportToCSV = async () => {
        try {
            // Создаем CSV из уже загруженных данных
            if (!stats || stats.length === 0) {
                setError("Нет данных для экспорта");
                return;
            }

            const csvHeaders = [
                "Медиа контент",
                "Сезон",
                "Название",
                ...(isAdmin ? ["ID в БД", "URL медиа"] : []),
                "Клиент",
                "Просмотры",
                "Зрители",
                ...(isAdmin ? ["Общий трафик (байты)"] : []),
                "Первый просмотр",
                "Последний просмотр",
            ];
            const csvRows = stats.map((item: any) => {
                // Определяем значения для столбцов
                // Сериал: grandparentMediaName (сериал) или mediaTitle (если нет grandparentMedia)
                const series = (() => {
                    if (item.grandparentMediaName) {
                        return item.grandparentMediaName;
                    }
                    if (item.parentMediaName && !item.grandparentMediaName) {
                        return item.mediaTitle || '—';
                    }
                    return item.mediaTitle || '—';
                })();
                // Сезон: parentMediaName только если есть grandparentMediaName (сериал)
                const season = item.grandparentMediaName && item.parentMediaName ? item.parentMediaName : '—';
                // Название: actualMediaName (эпизод) если есть parentMediaName, иначе mediaTitle
                const name = (() => {
                    if (item.parentMediaName) {
                        return item.actualMediaName || "Без названия";
                    }
                    return item.mediaTitle || item.actualMediaName || "Без названия";
                })();
                
                return [
                    series,
                    season,
                    name,
                    ...(isAdmin ? [formatMediaDbIds(item)] : []),
                    ...(isAdmin ? [getMediaUrl(item) || ""] : []),
                    item.clientName || "",
                    item.viewCount || "0",
                    item.uniqueViewers || "0",
                    ...(isAdmin ? [item.totalBytes || "0"] : []),
                    item.firstView ? new Date(item.firstView).toLocaleString("ru-RU") : "",
                    item.lastView ? new Date(item.lastView).toLocaleString("ru-RU") : "",
                ];
            });

            exportToCsv({
                filename: `media_stats_${new Date().toISOString().split("T")[0]}`,
                headers: csvHeaders,
                data: csvRows,
                separator: Constants.CSV_SEPARATOR,
            });
        } catch (err) {
            setError("Ошибка экспорта данных");
            console.error("Error exporting media stats:", err);
        }
    };

    const exportToExcelMedia = async () => {
        try {
            if (!stats || stats.length === 0) {
                setError("Нет данных для экспорта");
                return;
            }

            const headers = [
                "Медиа контент",
                "Сезон",
                "Название",
                ...(isAdmin ? ["ID в БД", "URL медиа"] : []),
                "Клиент",
                "Просмотры",
                "Зрители",
                ...(isAdmin ? ["Общий трафик (байты)"] : []),
                "Первый просмотр",
                "Последний просмотр",
            ];

            const data = stats.map((item: any) => {
                // Определяем значения для столбцов
                // Сериал: grandparentMediaName (сериал) или mediaTitle (если нет grandparentMedia)
                const series = (() => {
                    if (item.grandparentMediaName) {
                        return item.grandparentMediaName;
                    }
                    if (item.parentMediaName && !item.grandparentMediaName) {
                        return item.mediaTitle || '—';
                    }
                    return item.mediaTitle || '—';
                })();
                // Сезон: parentMediaName только если есть grandparentMediaName (сериал)
                const season = item.grandparentMediaName && item.parentMediaName ? item.parentMediaName : '—';
                // Название: actualMediaName (эпизод) если есть parentMediaName, иначе mediaTitle
                const name = (() => {
                    if (item.parentMediaName) {
                        return item.actualMediaName || "Без названия";
                    }
                    return item.mediaTitle || item.actualMediaName || "Без названия";
                })();
                
                return [
                    series,
                    season,
                    name,
                    ...(isAdmin ? [formatMediaDbIds(item)] : []),
                    ...(isAdmin ? [getMediaUrl(item) || ""] : []),
                    item.clientName || "",
                    item.viewCount || "0",
                    item.uniqueViewers || "0",
                    ...(isAdmin ? [item.totalBytes || "0"] : []),
                    item.firstView ? new Date(item.firstView).toLocaleString("ru-RU") : "",
                    item.lastView ? new Date(item.lastView).toLocaleString("ru-RU") : "",
                ];
            });

            const additionalInfo = [
                { label: "Период", value: `${formatDateForExcel(new Date(startDate || ""))} - ${formatDateForExcel(new Date(endDate || ""))}` },
                { label: "Поставщик", value: supplierStore.currentSupplier?.name || "Не выбран" },
                { label: "Всего записей", value: stats.length },
                { label: "Общее количество просмотров", value: totalViewCount },
                { label: "Зрителей", value: totalUniqueViewers }
            ];

            exportToExcel({
                filename: `media_stats_${new Date().toISOString().split("T")[0]}`,
                sheetName: "Статистика медиа",
                headers,
                data,
                additionalInfo
            });
        } catch (error) {
            console.error("Ошибка экспорта в Excel:", error);
            setError("Ошибка экспорта в Excel");
        }
    };

    useEffect(() => {
        fetchStats();
    }, [startDate, endDate, clientId, supplierStore.currentSupplier?.id]);

    // Используем totalViewCount из API если есть, иначе суммируем TOP 20
    const totalViews = totalViewCount || stats.reduce((sum, stat) => sum + Number(stat.viewCount), 0);
    const totalUniqueViewers =
        totalUniqueViewersCount ?? stats.reduce((sum, stat) => sum + Number(stat.uniqueViewers), 0);
    const totalBytes = stats.reduce((sum, stat) => sum + Number(stat.totalBytes || 0), 0);

    const formatBytes = (bytes: number): string => {
        if (bytes === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    // Функция для получения правильной ссылки: сначала эпизода, потом сериала, потом сезона
    // ВАЖНО: для эпизодов приоритет - actualMediaLink (файл эпизода из log.url), затем ссылка на сериал
    const getMediaUrl = (stat: MediaStat): string => {
        // Для эпизодов (есть parentMediaName) приоритет: actualMediaLink (файл эпизода) > grandparentMediaLink (сериал) > mediaUrl
        if (stat.parentMediaName) {
            // ПРИОРИТЕТ 1: actualMediaLink содержит ссылку на файл эпизода (из log.url)
            if (stat.actualMediaLink && !stat.actualMediaLink.includes('/season/')) {
                return stat.actualMediaLink;
            }
            // ПРИОРИТЕТ 2: grandparentMediaLink (ссылка на сериал)
            if (stat.grandparentMediaLink && !stat.grandparentMediaLink.includes('/season/')) {
                return stat.grandparentMediaLink;
            }
            // ПРИОРИТЕТ 3: mediaUrl (обработанная ссылка из бэкенда)
            if (stat.mediaUrl && !stat.mediaUrl.includes('/season/')) {
                return stat.mediaUrl;
            }
            // В крайнем случае используем parentMediaLink, но только если он не содержит /season/
            if (stat.parentMediaLink && !stat.parentMediaLink.includes('/season/')) {
                return stat.parentMediaLink;
            }
        } else {
            // Для фильмов используем mediaUrl напрямую
            if (stat.mediaUrl && !stat.mediaUrl.includes('/season/')) {
                return stat.mediaUrl;
            }
            if (stat.actualMediaLink && !stat.actualMediaLink.includes('/season/')) {
                return stat.actualMediaLink;
            }
        }
        return stat.mediaUrl || stat.actualMediaLink || "";
    };

    // Группировка данных по сериалам
    interface GroupedSeries {
        seriesId: string;
        seriesName: string;
        seriesLink: string | null;
        totalViews: number;
        totalViewers: number;
        hasDistinctViewers: boolean;
        episodes: MediaStat[];
    }

    const groupedData = React.useMemo(() => {
        const groups = new Map<string, GroupedSeries>();
        const films: MediaStat[] = []; // Отдельно храним фильмы
        
        stats.forEach((stat) => {
            // Определяем, является ли запись фильмом (без иерархии)
            const isFilm = !stat.grandparentMediaName && !stat.parentMediaName;
            
            if (isFilm) {
                // Фильмы не группируем, просто добавляем в отдельный массив
                films.push(stat);
                return;
            }
            
            // Для сериалов группируем по grandparentMediaName
            const seriesId = stat.grandparentMediaId?.toString() || stat.grandparentMediaName || "no-series";
            const seriesName = stat.grandparentMediaName || "Без сериала";
            // Для ссылки на сериал используем mediaUrl эпизода (который должен быть ссылкой на сериал) или grandparentMediaLink
            // Но только если mediaUrl не содержит /season/
            const seriesLink = (stat.mediaUrl && !stat.mediaUrl.includes('/season/')) ? stat.mediaUrl : (stat.grandparentMediaLink || null);
            
            if (!groups.has(seriesId)) {
                groups.set(seriesId, {
                    seriesId,
                    seriesName,
                    seriesLink,
                    totalViews: 0,
                    totalViewers: 0,
                    hasDistinctViewers: false,
                    episodes: [],
                });
            }
            
            const group = groups.get(seriesId)!;
            group.episodes.push(stat);
            group.totalViews += Number(stat.viewCount);
            const groupUniqueViewers = parseOptionalNumber(stat.groupUniqueViewers);
            if (groupUniqueViewers !== null) {
                group.totalViewers = groupUniqueViewers;
                group.hasDistinctViewers = true;
            } else if (!group.hasDistinctViewers) {
                group.totalViewers += Number(stat.uniqueViewers);
            }
        });
        
        // Сортируем эпизоды внутри каждого сериала по сезонам и номерам эпизодов
        groups.forEach((group) => {
            group.episodes.sort((a, b) => {
                // Сначала по сезону (parentMediaName)
                const seasonA = a.parentMediaName || '';
                const seasonB = b.parentMediaName || '';
                if (seasonA !== seasonB) {
                    return seasonA.localeCompare(seasonB);
                }
                // Затем по названию эпизода (actualMediaName)
                const nameA = a.actualMediaName || '';
                const nameB = b.actualMediaName || '';
                return nameA.localeCompare(nameB);
            });
        });
        
        // Сортируем фильмы по количеству просмотров
        films.sort((a, b) => Number(b.viewCount) - Number(a.viewCount));
        
        // Возвращаем сериалы и фильмы отдельно
        const seriesGroups = Array.from(groups.values()).sort((a, b) => b.totalViews - a.totalViews);
        
        // Если есть фильмы, создаем специальную группу "Фильмы" (но без возможности раскрытия)
        if (films.length > 0) {
            const filmsGroupUniqueViewers = films
                .map((film) => parseOptionalNumber(film.groupUniqueViewers))
                .find((value): value is number => value !== null);
            const filmsGroup: GroupedSeries = {
                seriesId: "films",
                seriesName: "Фильмы",
                seriesLink: null,
                totalViews: films.reduce((sum, film) => sum + Number(film.viewCount), 0),
                totalViewers:
                    filmsGroupUniqueViewers ?? films.reduce((sum, film) => sum + Number(film.uniqueViewers), 0),
                hasDistinctViewers: filmsGroupUniqueViewers !== undefined,
                episodes: films,
            };
            return [...seriesGroups, filmsGroup];
        }
        
        return seriesGroups;
    }, [stats]);

    const toggleSeries = (seriesId: string) => {
        setExpandedSeries((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(seriesId)) {
                newSet.delete(seriesId);
            } else {
                newSet.add(seriesId);
            }
            return newSet;
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" component="h2">
                    Статистика медиа
                </Typography>
                <Box>
                    {/* <Tooltip title="Обновить данные"> */}
                    <IconButton onClick={fetchStats} color="primary">
                        <RefreshIcon />
                    </IconButton>
                    {/* </Tooltip> */}
                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportToCSV} sx={{ ml: 1 }}>
                        Экспорт CSV
                    </Button>
                    <Button 
                        variant="contained" 
                        color="success" 
                        startIcon={<ExcelIcon />} 
                        onClick={exportToExcelMedia} 
                        sx={{ ml: 1 }}
                    >
                        Экспорт Excel
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Сводная статистика */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <PlayIcon color="primary" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography variant="h4" component="div">
                                        {totalViews.toLocaleString()}
                                    </Typography>
                                    <Typography color="textSecondary">Всего просмотров</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <PeopleIcon color="secondary" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography variant="h4" component="div">
                                        {totalUniqueViewers.toLocaleString()}
                                    </Typography>
                                    <Typography color="textSecondary">Зрителей</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                {isAdmin && (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center">
                                    <StorageIcon color="success" sx={{ mr: 1 }} />
                                    <Box>
                                        <Typography variant="h4" component="div">
                                            {formatBytes(totalBytes)}
                                        </Typography>
                                        {/* <Tooltip title="Общий объем данных, переданных при просмотре медиа контента (включая видео, аудио и другие файлы)"> */}
                                        <Typography color="textSecondary" sx={{ cursor: "help" }}>
                                            Общий трафик
                                        </Typography>
                                        {/* </Tooltip> */}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                {isAdmin && (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center">
                                    <Typography variant="h4" component="div">
                                        {stats.length}
                                    </Typography>
                                    <Box ml={1}>
                                        <Typography color="textSecondary">Уникальных медиа</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>

            {/* Информационный баннер с пояснением - скрыт для поставщиков */}
            {/* <Alert severity="info" sx={{ mb: 3, mx: 2 }}>
          <Typography variant="body2">
            <strong>Статистика медиа:</strong> Показывает все просмотры
            медиа-файлов на устройствах abox за выбранный период. Зрители могут
            пересекаться с другими вкладками.
          </Typography>
        </Alert> */}

            {/* Таблица статистики */}
            <Card>
                <CardContent>
                    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="50px"></TableCell>
                                    <TableCell>Медиа контент</TableCell>
                                    <TableCell>Сезон</TableCell>
                                    <TableCell>Название</TableCell>
                                    {isAdmin && <TableCell>ID в БД</TableCell>}
                                    <TableCell>Клиент</TableCell>
                                    <TableCell align="right">Просмотры</TableCell>
                                    <TableCell align="right">Зрители</TableCell>
                                    <TableCell>Первый просмотр</TableCell>
                                    <TableCell>Последний просмотр</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groupedData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={isAdmin ? 10 : 9} align="center">
                                            <Typography color="text.secondary">Нет данных</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    groupedData.map((group) => {
                                        const isFilmGroup = group.seriesId === "films";
                                        const isExpanded = expandedSeries.has(group.seriesId);
                                        const hasEpisodes = group.episodes.length > 0;
                                        
                                        // Для группы фильмов всегда показываем все фильмы (без группировки)
                                        if (isFilmGroup) {
                                            return (
                                                <React.Fragment key={group.seriesId}>
                                                    {/* Заголовок группы "Фильмы" */}
                                                    <TableRow sx={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
                                                        <TableCell></TableCell>
                                                        <TableCell>
                                                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                                                {group.seriesName}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell colSpan={2}></TableCell>
                                                        {isAdmin && <TableCell></TableCell>}
                                                        <TableCell>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Всего
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography variant="h6" color="primary">
                                                                {group.totalViews.toLocaleString()}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography variant="body1">
                                                                {group.totalViewers.toLocaleString()}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell colSpan={2}></TableCell>
                                                    </TableRow>
                                                    
                                                    {/* Все фильмы отображаются сразу (без раскрытия) */}
                                                    {group.episodes.map((stat, index) => (
                                                        <TableRow key={`film-${index}`} sx={{ pl: 4 }}>
                                                            <TableCell></TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">—</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">
                                                                    {stat.actualMediaName || stat.mediaTitle}
                                                                </Typography>
                                                                {isAdmin && (stat.actualMediaLink || stat.mediaUrl) && (
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="textSecondary"
                                                                        sx={{ maxWidth: 300, wordBreak: "break-all", mt: 0.5, fontSize: '0.75rem' }}
                                                                    >
                                                                        {stat.actualMediaLink || stat.mediaUrl}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            {isAdmin && <TableCell>{renderMediaDbIds(stat)}</TableCell>}
                                                            <TableCell>
                                                                <Chip label={stat.clientName || "—"} size="small" />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body1">
                                                                    {Number(stat.viewCount).toLocaleString()}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body1">
                                                                    {Number(stat.uniqueViewers).toLocaleString()}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {stat.firstView ? new Date(stat.firstView).toLocaleDateString('ru-RU') : "—"}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {stat.lastView ? new Date(stat.lastView).toLocaleDateString('ru-RU') : "—"}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        }
                                        
                                        // Для сериалов используем обычную логику с раскрытием
                                        return (
                                            <React.Fragment key={group.seriesId}>
                                                {/* Строка сериала */}
                                                <TableRow 
                                                    hover 
                                                    sx={{ 
                                                        backgroundColor: isExpanded ? 'rgba(0, 188, 212, 0.05)' : 'transparent',
                                                        cursor: hasEpisodes ? 'pointer' : 'default',
                                                        '&:hover': { backgroundColor: 'rgba(0, 188, 212, 0.1)' }
                                                    }}
                                                    onClick={() => hasEpisodes && toggleSeries(group.seriesId)}
                                                >
                                                    <TableCell>
                                                        {hasEpisodes && (
                                                            <IconButton size="small">
                                                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                            </IconButton>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                                            {group.seriesName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell colSpan={2}></TableCell>
                                                    {isAdmin && (
                                                        <TableCell>
                                                            {group.seriesId !== "films" && group.seriesId !== "no-series" ? (
                                                                <Chip label={`Сериал #${group.seriesId}`} size="small" color="secondary" variant="outlined" />
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </TableCell>
                                                    )}
                                                    <TableCell>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Всего
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="h6" color="primary">
                                                            {group.totalViews.toLocaleString()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body1">
                                                            {group.totalViewers.toLocaleString()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell colSpan={2}></TableCell>
                                                </TableRow>
                                                
                                                {/* Эпизоды сериала (показываются только если раскрыто) */}
                                                {isExpanded && group.episodes.map((stat, index) => (
                                                    <TableRow key={`${group.seriesId}-${index}`} sx={{ pl: 4 }}>
                                                        <TableCell></TableCell>
                                                        <TableCell></TableCell>
                                                        {/* Столбец Сезон */}
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {stat.grandparentMediaName && stat.parentMediaName ? stat.parentMediaName : '—'}
                                                            </Typography>
                                                        </TableCell>
                                                        {/* Столбец Название */}
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                                    {(() => {
                                                                        if (stat.parentMediaName) {
                                                                            return stat.actualMediaName || "Без названия";
                                                                        }
                                                                        return stat.mediaTitle || stat.actualMediaName || "Без названия";
                                                                    })()}
                                                </Typography>
                                                {isAdmin && (
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                                        sx={{ maxWidth: 300, wordBreak: "break-all", mt: 0.5, fontSize: '0.75rem' }}
                                                    >
                                                                        {getMediaUrl(stat)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        {isAdmin && <TableCell>{renderMediaDbIds(stat)}</TableCell>}
                                        <TableCell>
                                            <Chip
                                                label={stat.clientName}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="h6" color="primary">
                                                {Number(stat.viewCount).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body1">
                                                {Number(stat.uniqueViewers).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                                {new Date(stat.firstView).toLocaleDateString("ru-RU")}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                                {new Date(stat.lastView).toLocaleDateString("ru-RU")}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
});

export default MediaStats;
